import { Box, Button, ButtonProps, Checkbox, FormControlLabel, Stack, styled, Tooltip } from '@mui/material';
import Tab from '@mui/material/Tab';
import React, { useState, useEffect, useMemo, SyntheticEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ProductStore from '../../../AppData/_stores/ProductStore';
import UserStorePresetStore from '../../../UserData/_stores/Store/UserStorePresetStore';
import ListingStore from '../../../UserData/_stores/Store/ListingStore';
import DialogContent from '../../../Common/Dialog/DialogContent';
import DialogActions from '../../../Common/Dialog/DialogActions';
import DialogButton from '../../../Common/Buttons/DialogButton';
import { EditProductListing } from '../../../UserData/_actions/Store/StoreProductListingActions';
import { SaveStorePreset } from '../../../UserData/_actions/Store/UserStorePresetActions';
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import EditProductDetails from './EditProductDetails';
import EditProductPrices from './EditProductPrices';
import { TabContext, TabList } from '@mui/lab'
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { StoreListingValidation } from './Utils/StoreListingValidation';
import TabLabel from '../../../Common/Tabs/TabLabel';
import { StoreListingVariantPrices } from './Utils/StoreListingVariantPrices';

type EditProductTab = 'details' | 'prices'

export type Props = {
	index: number
	handleStep: (step: number) => void
	massPresets?: boolean
	isSingle?: boolean
	onDialogClose: () => void
	onDialogCloseWithConfirm: () => void
	product?: ProductStore
	storePreset?: UserStorePresetStore
	store: string
	backButtonLabel?: string
	cancelBehavior?: 'back' | 'close'
	submitLabel?: string
	submitColor?: ButtonProps['color']
	showStorePageField?: boolean
	onSubmitListing?: (formListing: ListingStore, listing: ListingStore) => void
}

const TabPanels = styled('div')({
	display: 'grid',
	alignItems: 'start',
})

const StyledTabPanel = styled('div', {
	shouldForwardProp: prop => prop !== 'selected',
})<{ selected: boolean }>(({ selected }) => ({
	gridArea: '1 / 1',
	minWidth: 0,
	pointerEvents: selected ? 'auto' : 'none',
	visibility: selected ? 'visible' : 'hidden',
}))

interface EditProductTabPanelProps {
	children: React.ReactNode
	selectedTab: EditProductTab
	value: EditProductTab
}

function EditProductTabPanel(props: EditProductTabPanelProps) {
	const selected = props.selectedTab === props.value

	return <StyledTabPanel
		aria-hidden={!selected}
		role="tabpanel"
		selected={selected}
	>
		{props.children}
	</StyledTabPanel>
}

const ApplyToAllWrapper = styled('div')({
	marginTop: 24
})

export default function EditProduct(props: Props) {
	const dispatch = useAppDispatch()
	const [t] = useTranslation('account-v2')
	const saving = useAppSelector(state => !!state.get('UIData').get('loaders').get('edit_store_preset'))

	const listings = useAppSelector(state => state.get('UIData').get('account').get('storeProductListings'))
	const listing = useAppSelector(state => state.get('UIData').get('account').get('storeProductListings').get(state.get('UIData').get('account').get('storeProductSelectedListing')))
	const productDesign = useAppSelector(state => listing ? state.get('userData').get('productDesigns').get('data').get(String(listing.get('id'))) : undefined)
	const product = useAppSelector(state => props.product ? props.product : (listing ? state.get('appData').get('products').get(String(listing.get('id_product'))) : undefined))

	const [tab, setTab] = useState<EditProductTab>('details')
	const [hasOpenedPrices, setHasOpenedPrices] = useState(false)
	const [disableButtons, setDisableButtons] = useState(false)
	const [applyToAll, setApplyToAll] = useState(false)
	const [formListing, setFormListing] = useState(new ListingStore())

	useEffect(() => {
		if (props.massPresets) {
			if (props.storePreset) {
				setFormListing(new ListingStore({
					description: props.storePreset.get('description'),
					price: props.storePreset.get('price'),
					disabled_preview_ids: props.storePreset.get('disabled_preview_ids'),
					disabled_option_ids: props.storePreset.get('disabled_option_ids'),
					variant_prices: product ? StoreListingVariantPrices.normalizeOverrides(props.storePreset.get('variant_prices'), product, product.get('availableOptionTypes')) : props.storePreset.get('variant_prices'),
				}))
			}
		} else {
			if (listing) {
				setFormListing(new ListingStore({
					title: listing.get('title'),
					description: listing.get('description'),
					price: listing.get('price'),
					squarespace_store_page_id: listing.get('squarespace_store_page_id'),
					update_preset: listing.get('update_preset'),
					disabled_preview_ids: listing.get('disabled_preview_ids'),
					disabled_option_ids: listing.get('disabled_option_ids'),
					variant_prices: listing.get('variant_prices'),
				}))
			}
		}
	}, [listing, product, props.storePreset, props.massPresets])

	const onChangeTab = useCallback((e: React.SyntheticEvent, value: EditProductTab) => {
		setTab(value)
		if (value === 'prices') setHasOpenedPrices(true)
	}, []);

	const handleGoPreviousState = useCallback(() => {
		props.handleStep(props.index - 1)
	}, [props.handleStep, props.index])

	const handleCancelChanges = useCallback(() => {
		if (props.massPresets) {
			props.onDialogCloseWithConfirm()
			return
		}

		if (props.cancelBehavior === 'close') {
			props.onDialogCloseWithConfirm()
			return
		}

		handleGoPreviousState()
	}, [handleGoPreviousState, props.cancelBehavior, props.massPresets, props.onDialogCloseWithConfirm])

	const previews = useMemo(() => props.massPresets ? product?.get('previews') : productDesign?.get('previews'), [props.massPresets, product, productDesign])
	const availableOptionTypes = useMemo(() => props.massPresets ? product?.get('availableOptionTypes') : productDesign?.get('availableOptionTypes'), [props.massPresets, product, productDesign])

	const handleSaveChanges = useCallback(() => {
		if (saving) return;

		const savedFormListing = formListing.set(
			'variant_prices',
			StoreListingVariantPrices.filterEnabled(
				product && availableOptionTypes ? StoreListingVariantPrices.normalizeOverrides(formListing.get('variant_prices'), product, availableOptionTypes) : formListing.get('variant_prices'),
				formListing.get('disabled_option_ids'),
			),
		);

		if (props.massPresets) {
			if (!props.storePreset) return;

			let jsonData = JSON.stringify({
				id: props.storePreset.get('id'),
				id_product: props.storePreset.get('id_product'),
				description: savedFormListing.get('description'),
				price: savedFormListing.get('price'),
				disabled_preview_ids: savedFormListing.get('disabled_preview_ids').valueSeq().toArray(),
				disabled_option_ids: savedFormListing.get('disabled_option_ids').valueSeq().toArray(),
				variant_prices: savedFormListing.get('variant_prices'),
			});

			dispatch(SaveStorePreset(props.store, jsonData).set({
				onSuccess: props.onDialogClose,
			}))
		} else {
			if (!listing) return;

			if (props.onSubmitListing) {
				props.onSubmitListing(savedFormListing, listing)
				return
			}

			dispatch(EditProductListing(
				listing.get('id_product_design'),
				listing.get('id_product'),
				savedFormListing.get('title'),
				savedFormListing.get('description'),
				savedFormListing.get('price'),
				savedFormListing.get('update_preset'),
				true,
				savedFormListing.get('disabled_preview_ids').valueSeq().toArray(),
				savedFormListing.get('disabled_option_ids').valueSeq().toArray(),
				savedFormListing.get('variant_prices'),
				savedFormListing.get('squarespace_store_page_id'),
			))

			if (applyToAll) {
				// Update other listings
				let tmpListings = listings.filter((tmpListing) =>
					tmpListing.get('id_product') === listing.get('id_product')
					&& tmpListing.get('id') !== listing.get('id')
				);

				tmpListings.forEach((tmpListing) => {
					dispatch(EditProductListing(
						tmpListing.get('id_product_design'),
						tmpListing.get('id_product'),
						tmpListing.get('title'),
						savedFormListing.get('description'),
						savedFormListing.get('price'),
						savedFormListing.get('update_preset'),
						true,
						savedFormListing.get('disabled_preview_ids').valueSeq().toArray(),
						savedFormListing.get('disabled_option_ids').valueSeq().toArray(),
						savedFormListing.get('variant_prices'),
						savedFormListing.get('squarespace_store_page_id'),
					))
				});
			}

			props.massPresets ? props.handleStep(props.index + 1) : props.handleStep(props.index - 1);
		}
	}, [
		saving,
		availableOptionTypes,
		product,
		applyToAll,
		dispatch,
		formListing,
		listing,
		listings,
		props.handleStep,
		props.index,
		props.massPresets,
		props.onDialogClose,
		props.onSubmitListing,
		props.store,
		props.storePreset,
	])

	const handleDisableButtons = useCallback(() => {
		setDisableButtons(true)
	}, [])

	const handleEnableButtons = useCallback(() => {
		setDisableButtons(false)
	}, [])

	const nbProducts = useMemo(() => {
		return !props.massPresets ? listings.filter((tmpListing) =>
			tmpListing.get('id_product') === listing?.get('id_product')
		).count() : 0
	}, [props.massPresets, listings, listing])



	const listingValidation = useMemo(() => StoreListingValidation.validate({
		store: props.store,
		listing: formListing,
		product,
		availableOptionTypes,
		previews,
		massPresets: props.massPresets,
		showStorePageField: props.showStorePageField,
	}), [availableOptionTypes, formListing, previews, product, props.massPresets, props.showStorePageField, props.store])

	const priceErrorMessage = useMemo(() => {
		const priceError = listingValidation.fieldErrors.prices;

		if (priceError?.code === 'price_below_minimum') {
			return t('Prices must be at least {{minimumPrice}}.', { minimumPrice: priceError.data?.minimumPrice });
		}

		return undefined;
	}, [listingValidation.fieldErrors.prices, t]);
	const detailsTabHasErrors = useMemo(() => listingValidation.errors.some(error => error.field !== 'prices'), [listingValidation]);
	const pricesTabHasErrors = useMemo(() => disableButtons || !!listingValidation.fieldErrors.prices, [disableButtons, listingValidation]);
	const saveDisabled = useMemo(() => pricesTabHasErrors || detailsTabHasErrors, [detailsTabHasErrors, pricesTabHasErrors]);
	const saveErrorTooltip = useMemo(() => {
		if (detailsTabHasErrors && pricesTabHasErrors) {
			return t('Please fix the detail and pricing errors before saving.');
		}

		if (detailsTabHasErrors) {
			return t('Please fix the detail errors before saving.');
		}

		return t('Please fix the pricing errors before saving.');
	}, [detailsTabHasErrors, pricesTabHasErrors, t]);

	const onChangeApplyToAll = useCallback((e: SyntheticEvent<Element>, checked: boolean) => setApplyToAll(checked), [])

	if (!product) return null;

	return <>
		<TabContext value={tab}>
			<Box
				component="div"
				sx={{
					background: 'rgba(0, 0, 0, 0.06)',
				}}
			>
				{!props.massPresets && (props.backButtonLabel || !props.isSingle) ? <div style={{
					padding: '10px 25px 0',
				}}>
					<Button
						variant="text"
						color="secondary"
						startIcon={<KeyboardBackspaceRoundedIcon />}
						onClick={handleGoPreviousState}
					>{props.backButtonLabel ?? t('Back to Selected products')}</Button>
				</div> : null}

				<TabList
					onChange={onChangeTab}
					sx={{
						px: {
							xs: 3,
							sm: 4,
						}
					}}
				>
					<Tab label={<TabLabel label={t('Details')} error={detailsTabHasErrors} />} value="details" />
					<Tab label={<TabLabel label={t('Prices')} error={pricesTabHasErrors} />} value="prices" />
				</TabList>
			</Box>

			<DialogContent sx={{ mt: 0 }}>
				<TabPanels>
					<EditProductTabPanel selectedTab={tab} value="details">
						<EditProductDetails
							validation={listingValidation}
							store={props.store}
							product={product}
							productDesign={productDesign}
							listing={formListing}
							onChangeListing={setFormListing}
							massPresets={props.massPresets}
							showStorePageField={props.showStorePageField}
						/>
					</EditProductTabPanel>
					<EditProductTabPanel selectedTab={tab} value="prices">
						{hasOpenedPrices && <EditProductPrices
							product={product}
							productDesign={productDesign}
							price={formListing.get('price')}
							variantPrices={formListing.get('variant_prices')}
							disabledOptionIds={formListing.get('disabled_option_ids')}
							onChangeListing={setFormListing}
							disableButtons={handleDisableButtons}
							enableButtons={handleEnableButtons}
							massPresets={props.massPresets}
							error={disableButtons || !!listingValidation.fieldErrors.prices}
							errorMessage={priceErrorMessage}
						/>}
					</EditProductTabPanel>
				</TabPanels>

				{!props.massPresets && nbProducts > 1 && <ApplyToAllWrapper>
					<FormControlLabel
						control={<Checkbox />}
						checked={applyToAll}
						label={t('Apply changes to all ({{nbProducts}}) {{product}} products you selected', { nbProducts: nbProducts, product: product.get('name') })}
						onChange={onChangeApplyToAll}
					/>
				</ApplyToAllWrapper>}
			</DialogContent>

			<DialogActions>
				<DialogButton
					color="secondary"
					disabled={saving}
					onClick={handleCancelChanges}
				>{t('Cancel')}</DialogButton>

				<Stack direction="row" spacing={1} alignItems="center">
					<Tooltip title={saveDisabled ? saveErrorTooltip : null}>
						<Box component="span" sx={{ display: 'inline-flex' }}>
							<DialogButton
								color={props.submitColor ?? 'primary'}
								onClick={handleSaveChanges}
								disabled={saveDisabled || saving}
								startIcon={saveDisabled ? <Box component="span" sx={{ display: 'inline-flex' }} aria-label={saveErrorTooltip}>
									<ErrorOutlineIcon color="error" />
								</Box> : null}
							>{props.submitLabel ?? t('Save changes')}</DialogButton>
						</Box>
					</Tooltip>
				</Stack>
			</DialogActions>
		</TabContext>
	</>
}
